import swal from "@sweetalert/with-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { FiUser } from "react-icons/fi";
import ReactPaginate from "react-paginate";
import { useDispatch } from "react-redux";
import BigLoader from "../../components/BigLoader";
import Button from "../../components/Button";
import Card from "../../components/Card";
import CourseList from "../../components/CourseList";
import Dropzone from "../../components/Dropzone";
import Loader from "../../components/Loader";
import Modal from "../../components/Modal";
import TextInput from "../../components/TextInput";
import useDebounce from "../../debounce";
import { setInterface } from "../../redux/slices/interfaceSlice";
import service from "../../service";

export default function Course() {
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [getLoading, setGetLoading] = useState(true);
  const [postLoading, setPostLoading] = useState(false);
  const _modalCover = useRef();
  const _modalMentor = useRef();
  const _modalPdf = useRef();
  const { control, handleSubmit, watch, reset, register } = useForm();
  const [id, setId] = useState(0);
  const debounce = useDebounce((text) => getUser(text));

  const changeCover = ({ cover }) => {
    setPostLoading(true);

    const formData = new FormData();
    formData.append("cover", cover);

    service
      .put(`/api/course/${id}/cover`, formData)
      .then(() => {
        _modalCover.current.hide();
        setPostLoading(false);
        getData();
        swal("Berhasil", `Cover berhasil diganti`, "success");
      })
      .catch(() => {
        setPostLoading(false);
      });
  };

  const changePdf = ({ pdf, pdf_password }) => {
    setPostLoading(true);

    const formData = new FormData();
    formData.append("pdf", pdf);
    if (pdf_password) {
      formData.append("pdf_password", pdf_password);
    }

    service
      .put(`/api/course/${id}/pdf`, formData)
      .then(() => {
        _modalPdf.current.hide();
        setPostLoading(false);
        getData();
        swal("Berhasil", `PDF berhasil diganti`, "success");
      })
      .catch(() => {
        setPostLoading(false);
      });
  };

  const deleteMentor = (id, mentor) => {
    swal({
      title: "Anda yakin?",
      text: "Anda akan menghapus mentor ini",
      icon: "warning",
      buttons: ["Batal", true],
      dangerMode: true,
      cancelButton: "Batal",
    }).then((value) => {
      if (value) {
        setPostLoading(true);
        service
          .delete(`/api/course/${id}/mentor/${mentor}`)
          .then(() => {
            setPostLoading(false);
            getData();
            swal("Berhasil", `Mentor berhasil dihapus`, "success");
          })
          .catch(() => {
            setPostLoading(false);
          });
      }
    });
  };

  const addMentor = (mentor) => {
    setPostLoading(true);
    service
      .post(`/api/course/${id}/mentor`, {
        mentor,
      })
      .then(() => {
        _modalMentor.current.hide();
        setPostLoading(false);
        getData();
        swal("Berhasil", `Mentor berhasil diganti`, "success");
      })
      .catch(() => {
        setPostLoading(false);
      });
  };

  const getUser = (q = "") => {
    service
      .get("/api/user", {
        params: {
          q,
          mentor: true,
        },
      })
      .then((response) => {
        setUserLoading(false);
        setUsers(response.data);
      })
      .catch(() => {
        setUserLoading(false);
        setUsers([]);
      });
  };

  const getData = () => {
    setGetLoading(true);
    service
      .get("/api/course", {
        params: {
          page,
        },
      })
      .then((response) => {
        setGetLoading(false);
        setData(response.data.data);
        setTotal(response.data.total);
      })
      .catch((e) => {
        setGetLoading(false);
      });
  };

  const onDelete = (id) => {
    swal({
      title: "Anda yakin?",
      text: "Anda akan menghapus course ini",
      icon: "warning",
      buttons: ["Batal", true],
      dangerMode: true,
      cancelButton: "Batal",
    }).then((value) => {
      if (value) {
        setPostLoading(true);
        service
          .delete(`/api/course/${id}`)
          .then(() => {
            getData();
            setPostLoading(false);
            swal("Berhasil", `Course berhasil dihapus`, "success");
          })
          .catch((e) => {
            setPostLoading(false);
          });
      }
    });
  };

  useEffect(() => {
    getData();
  }, [page]);

  useEffect(() => {
    dispatch(
      setInterface({
        active: "Online Course",
        pageTitle: "Online Course",
        rightButton: {
          to: "/course/add",
          label: "Tambah",
          color: "green",
          withPlusIcon: true,
        },
      })
    );
  }, [dispatch]);
  return (
    <Fragment>
      <Helmet>
        <title>Online Course</title>
      </Helmet>
      <Card>
        {getLoading ? (
          <div className="h-64 flex justify-center items-center flex-col">
            <Loader className="bg-black-100" />
            <span className="mt-3 text-black-300 text-sm">Memuat</span>
          </div>
        ) : data.length ? (
          <Fragment>
            {data.map((item, index) => (
              <CourseList
                id={item.id}
                key={`${index}`}
                title={item.title}
                mentor={item.users}
                imgSrc={item.cover}
                editLink={`/course/edit/${item.id}`}
                onChangeCover={() => {
                  setId(item.id);
                  reset({ cover: null });
                  _modalCover.current.show();
                }}
                onChangePdf={() => {
                  setId(item.id);
                  reset({ pdf: null, pdf_password: "" });
                  _modalPdf.current.show();
                }}
                onAddMentor={() => {
                  setId(item.id);
                  _modalMentor.current.show();
                }}
                onDeleteMentor={(id) => deleteMentor(item.id, id)}
              />
            ))}
            <ReactPaginate
              pageCount={total}
              pageRangeDisplayed={5}
              marginPagesDisplayed={2}
              initialPage={page - 1}
              containerClassName="flex flex-wrap mt-6 gap-1"
              previousLinkClassName="border border-white-300 rounded-sm py-1.5 px-3 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 block"
              nextLinkClassName="border border-white-300 rounded-sm py-1.5 px-3 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 block"
              pageLinkClassName="border border-white-300 rounded-sm py-1.5 px-3 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 block"
              activeLinkClassName="bg-primary-500 border-primary-600 text-white-100"
              onPageChange={({ selected }) => setPage(selected + 1)}
            />
          </Fragment>
        ) : (
          <div className="text-center flex flex-col justify-center items-center h-40 text-black-300">
            <p>Belum ada Online Course. Klik Tambah untuk membuat baru.</p>
          </div>
        )}
      </Card>
      <Modal title="Ganti Cover" ref={_modalCover}>
        <form onSubmit={handleSubmit(changeCover)}>
          <Controller
            name="cover"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange } }) => (
              <Dropzone
                containerClassName="mb-4"
                onChange={(accepted) => {
                  onChange(accepted[0]);
                }}
                dropzone={{ accept: "image/jpeg, image/png" }}
              />
            )}
          />
          {watch("cover") ? (
            <Fragment>
              <span className="text-sm font-semibold text-black-300 mb-2 block">Preview</span>
              <div className="flex justify-center mb-4">
                <img
                  src={URL.createObjectURL(watch("cover"))}
                  alt="Preview Cover"
                  className="w-28 h-28 object-cover"
                />
              </div>
            </Fragment>
          ) : null}
          <Button type="submit" color="blue" className="w-28">
            Simpan
          </Button>
        </form>
      </Modal>
      <Modal title="Ganti PDF" ref={_modalPdf}>
        <form onSubmit={handleSubmit(changePdf)}>
          <Controller
            name="pdf"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange } }) => (
              <Dropzone
                label="File PDF"
                containerClassName="mb-4"
                onChange={(accepted) => {
                  onChange(accepted[0]);
                }}
                dropzone={{ accept: "application/pdf" }}
              />
            )}
          />
          <TextInput
            label="Password PDF (opsional, jika PDF terkunci)"
            containerClassName="mb-4"
            type="password"
            autoComplete="new-password"
            {...register("pdf_password")}
          />
          <Button type="submit" color="blue" className="w-28">
            Simpan
          </Button>
        </form>
      </Modal>
      <Modal title="Pilih Mentor" ref={_modalMentor}>
        <TextInput
          containerClassName="mb-4"
          placeholder="Cari pengguna"
          onChange={(e) => {
            setUserLoading(true);
            debounce(e.target.value);
          }}
        />
        {userLoading ? (
          <div className="flex justify-center h-32">
            <Loader className="bg-black-200" />
          </div>
        ) : (
          users.map((item, index) => (
            <button
              type="button"
              onClick={() => {
                addMentor(item.id);
              }}
              key={`user-${index}`}
              className="w-full text-left py-3 border-b border-white-200 last:border-b-0 hover:bg-white-200 transition-colors flex items-center rounded-sm px-2"
            >
              <span className="w-10 h-10 rounded-full bg-white-200 flex items-center justify-center mr-3 text-black-300">
                <FiUser size={18} />
              </span>
              <div className="flex-1">
                <span className="font-bold text-black-200">
                  {item.fullname}
                </span>
                <br />
                <span className="text-sm text-black-300">
                  {item.username}
                </span>
              </div>
            </button>
          ))
        )}
      </Modal>
      {postLoading ? <BigLoader /> : null}
    </Fragment>
  );
}
